"use client";

import React, {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";

type Option = { label: React.ReactNode; value: string };
type CustomDropdownProps = {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
};

export default function CustomDropdown({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  id,
  ariaLabel,
  disabled = false,
}: CustomDropdownProps) {
  const uniqueId = useId();
  const listId = `${uniqueId}-list`;
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [styles, setStyles] = useState<CSSProperties>({});
  const [direction, setDirection] = useState<"ltr" | "rtl">("ltr");
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const typeahead = useRef({ text: "", at: 0 });
  const reduced = useReducedMotion();
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const position = () => {
      if (!trigger.current) return;
      const rect = trigger.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const width = viewport?.width || window.innerWidth;
      const availableBelow = height - rect.bottom - 12;
      const above =
        availableBelow < Math.min(240, options.length * 44) &&
        rect.top > availableBelow;
      setDirection(
        getComputedStyle(trigger.current).direction === "rtl" ? "rtl" : "ltr",
      );
      setStyles({
        position: "fixed",
        left: Math.max(8, Math.min(rect.left, width - rect.width - 8)),
        width: Math.min(rect.width, width - 16),
        maxHeight: Math.max(
          44,
          Math.min(280, above ? rect.top - 12 : availableBelow),
        ),
        ...(above
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        zIndex: 10000,
      });
    };
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !list.current?.contains(target))
        setOpen(false);
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    window.visualViewport?.addEventListener("resize", position);
    document.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
      window.visualViewport?.removeEventListener("resize", position);
      document.removeEventListener("pointerdown", outside);
    };
  }, [open, options.length]);
  useEffect(() => {
    if (open && highlighted >= 0)
      document
        .getElementById(`${listId}-${highlighted}`)
        ?.scrollIntoView({ block: "nearest" });
  }, [open, highlighted, listId]);
  const choose = (index: number) => {
    if (!options[index]) return;
    onChange(options[index].value);
    setOpen(false);
    trigger.current?.focus();
  };
  const reveal = () => {
    if (disabled || !options.length) return;
    setHighlighted(
      Math.max(
        0,
        options.findIndex((option) => option.value === value),
      ),
    );
    setOpen(true);
  };
  return (
    <div className={`relative min-w-0 ${className}`}>
      <button
        ref={trigger}
        id={id}
        type="button"
        role="combobox"
        aria-label={ariaLabel || placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && highlighted >= 0 ? `${listId}-${highlighted}` : undefined
        }
        disabled={disabled || !options.length}
        onClick={() => (open ? setOpen(false) : reveal())}
        onKeyDown={(event) => {
          if (event.key === "Tab") {
            setOpen(false);
            return;
          }
          if (event.key === "Escape") {
            if (open) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
            }
            return;
          }
          if (
            ["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(
              event.key,
            )
          ) {
            event.preventDefault();
            if (!open) {
              reveal();
              return;
            }
            if (event.key === "Enter" || event.key === " ") {
              choose(highlighted);
              return;
            }
            setHighlighted((index) =>
              event.key === "Home"
                ? 0
                : event.key === "End"
                  ? options.length - 1
                  : (index +
                      (event.key === "ArrowUp" ? -1 : 1) +
                      options.length) %
                    options.length,
            );
          } else if (
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey
          ) {
            const now = Date.now();
            typeahead.current = {
              text:
                (now - typeahead.current.at < 700
                  ? typeahead.current.text
                  : "") + event.key.toLocaleLowerCase(),
              at: now,
            };
            const index = options.findIndex(
              (option) =>
                typeof option.label === "string" &&
                option.label
                  .toLocaleLowerCase()
                  .startsWith(typeahead.current.text),
            );
            if (index >= 0) {
              setOpen(true);
              setHighlighted(index);
            }
          }
        }}
        className="text-slateCustom focus-visible:ring-pinkCustom/30 hover:border-slateCustom/30 flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-start text-sm transition-colors outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="min-w-0 flex-1 truncate">
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
        />
      </button>
      {typeof document !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <motion.ul
                  ref={list}
                  id={listId}
                  role="listbox"
                  aria-label={ariaLabel || placeholder}
                  dir={direction}
                  initial={reduced ? false : { opacity: 0, y: -3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12 }}
                  style={styles}
                  className="text-slateCustom m-0 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
                >
                  {options.map((option, index) => (
                    <li
                      key={option.value}
                      id={`${listId}-${index}`}
                      role="option"
                      aria-selected={option.value === value}
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={() => choose(index)}
                      onPointerMove={() => setHighlighted(index)}
                      className={`flex min-h-11 cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm ${index === highlighted ? "bg-grayCustom" : ""} ${option.value === value ? "text-pinkCustom font-semibold" : ""}`}
                    >
                      <span className="min-w-0 break-words">
                        {option.label}
                      </span>
                      {option.value === value ? (
                        <Check
                          aria-hidden="true"
                          className="h-4 w-4 shrink-0"
                        />
                      ) : null}
                    </li>
                  ))}
                </motion.ul>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </div>
  );
}
